import { useEffect, useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  CalendarRange, Download, Filter, Loader2, PrinterIcon,
  ShieldAlert, CheckCircle, Clock, AlertCircle,
  ChevronLeft, ChevronRight, Mail, Search, X, User, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { AlarmSite, ArcEvent } from '@asi/shared';
import {
  fetchHistoryEvents, fetchAllSites, getCidDescription,
  subscribeToArcEvents,
  type HistoryFilters, type HistoryEvent,
} from '@/services/arc';
import { ArcSubNav } from '@/components/ArcSubNav';

// ─── Constants ────────────────────────────────────────────────

const PAGE_SIZE = 100;

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'text-red-600 font-bold',
  ALARM:    'text-orange-500 font-semibold',
  WARNING:  'text-yellow-600',
  INFO:     'text-blue-500',
};

const SEVERITY_LABELS: Record<string, string> = {
  CRITICAL: 'Crítico', ALARM: 'Alarma', WARNING: 'Advertencia', INFO: 'Información',
};

const CATEGORY_LABELS: Record<string, string> = {
  alarm: 'Alarma', restore: 'Restauración', open_close: 'Apertura/Cierre',
  trouble: 'Falla', test: 'Prueba', system: 'Sistema', bypass: 'Bypass',
};

const STATUS_CFG = {
  resolved:   { label: 'Cerrado',    cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  acknowledged: { label: 'Reconocido', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  pending:    { label: 'Pendiente',  cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
};

// ─── Helpers ──────────────────────────────────────────────────

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function daysAgoStr(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

function fmtDatetime(iso: string) {
  return new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

function fmtResponseTime(s: number | null) {
  if (s === null) return '-';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function getStatus(e: HistoryEvent): keyof typeof STATUS_CFG {
  if (e.resolved_at) return 'resolved';
  if (e.acknowledged_at) return 'acknowledged';
  return 'pending';
}

// ─── Export helpers ─────────────────────────────────────────

function buildExportRows(events: HistoryEvent[]) {
  return events.map(e => ({
    'Fecha/Hora (recibido)': fmtDatetime(e.received_at),
    'Fecha/Hora (panel)': fmtDatetime(e.occurred_at),
    'Cliente':      e.alarm_sites?.name ?? 'Sin asignar',
    'Cuenta':       e.alarm_sites?.account_number ?? '',
    'Código CID':   e.cid_code,
    'Descripción':  getCidDescription(e.cid_code),
    'Severidad':    SEVERITY_LABELS[e.severity] ?? e.severity,
    'Categoría':    CATEGORY_LABELS[e.event_category] ?? e.event_category,
    'Zona':         e.zone_no != null ? `Z${e.zone_no}${e.zone_name ? ` - ${e.zone_name}` : ''}` : '-',
    'Estado':       STATUS_CFG[getStatus(e)]?.label ?? '-',
    'T.Respuesta':  fmtResponseTime(e.response_time_seconds),
    'Resolución':   e.resolution ?? '',
  }));
}

function exportExcel(events: HistoryEvent[]) {
  const rows = buildExportRows(events);
  const ws = XLSX.utils.json_to_sheet(rows);
  const colWidths = Object.keys(rows[0] ?? {}).map(k => ({ wch: Math.max(k.length, 14) }));
  ws['!cols'] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Bitácora ARC');
  XLSX.writeFile(wb, `bitacora-arc-${todayStr()}.xlsx`);
}

function exportCSV(events: HistoryEvent[]) {
  const headers = [
    'Fecha/Hora', 'Cliente', 'Cuenta', 'Código CID', 'Descripción',
    'Severidad', 'Categoría', 'Zona', 'Estado', 'T.Respuesta', 'Resolución',
  ];
  const rows = buildExportRows(events).map(r => Object.values(r));

  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');

  // BOM for Excel UTF-8 compatibility
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bitacora-arc-${todayStr()}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ─── Print ────────────────────────────────────────────────────

function handlePrint() {
  window.print();
}

// ─── Email ────────────────────────────────────────────────────

function buildMailto(events: HistoryEvent[], filters: HistoryFilters) {
  const range = `${filters.dateFrom} al ${filters.dateTo}`;
  const total = events.length;
  const critical = events.filter(e => e.severity === 'CRITICAL').length;
  const resolved = events.filter(e => e.resolved_at).length;
  const subject = encodeURIComponent(`Historial ARC — ${range}`);
  const body = encodeURIComponent(
    `Historial de eventos ARC\n` +
    `Período: ${range}\n` +
    `Total eventos: ${total}\n` +
    `Críticos: ${critical}\n` +
    `Cerrados: ${resolved}\n\n` +
    `Adjunto archivo CSV con el detalle completo.\n\n` +
    `— Sistema ASI Security`
  );
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
}

// ─── Event Detail Modal ─────────────────────────────────────

function HistoryEventModal({ event, onClose }: { event: HistoryEvent; onClose: () => void }) {
  const status = getStatus(event);
  const statusCfg = STATUS_CFG[status];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-background border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-muted">
              <FileText className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold">Detalle de evento</h3>
              <p className="text-sm text-muted-foreground font-mono">{event.cid_code} · {getCidDescription(event.cid_code)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Status badge */}
          <div className="flex items-center justify-between">
            <span className={cn('text-sm px-3 py-1 rounded-full font-medium', statusCfg.cls)}>
              {statusCfg.label}
            </span>
            <span className={cn('text-sm font-semibold', SEVERITY_COLORS[event.severity])}>
              {SEVERITY_LABELS[event.severity] ?? event.severity}
            </span>
          </div>

          {/* Grid info */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Fecha y hora</p>
              <p className="font-mono">{fmtDatetime(event.received_at)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Cliente / Sitio</p>
              <p className="font-semibold">{event.alarm_sites?.name ?? <span className="text-muted-foreground italic">Sin asignar</span>}</p>
              {event.alarm_sites?.account_number && (
                <p className="font-mono text-xs text-muted-foreground">[{event.alarm_sites.account_number}]</p>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Zona</p>
              <p>{event.zone_no != null ? `Z${event.zone_no}${event.zone_name ? ` — ${event.zone_name}` : ''}` : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Categoría</p>
              <p>{CATEGORY_LABELS[event.event_category] ?? event.event_category}</p>
            </div>
            {event.acknowledged_at && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Reconocido</p>
                <p className="font-mono text-xs">{fmtDatetime(event.acknowledged_at)}</p>
                {event.ack_profile && (
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {event.ack_profile.full_name ?? event.ack_profile.email}
                  </p>
                )}
              </div>
            )}
            {event.resolved_at && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Cerrado</p>
                <p className="font-mono text-xs">{fmtDatetime(event.resolved_at)}</p>
                {event.res_profile && (
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {event.res_profile.full_name ?? event.res_profile.email}
                  </p>
                )}
              </div>
            )}
            {event.response_time_seconds !== null && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">T. respuesta</p>
                <p className="font-semibold">{fmtResponseTime(event.response_time_seconds)}</p>
              </div>
            )}
          </div>

          {/* Resolution notes */}
          {event.resolution && (
            <div className="rounded-lg bg-muted/50 border px-4 py-3">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                Notas de procesamiento
              </p>
              <p className="text-sm whitespace-pre-wrap">{event.resolution}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────

function StatBadge({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="text-center">
      <div className={cn('text-2xl font-bold', color)}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function ArcHistoryPage() {
  const [filters, setFilters] = useState<HistoryFilters>({
    dateFrom: daysAgoStr(7),
    dateTo: todayStr(),
    siteId: '',
    severity: '',
    category: '',
    status: 'all',
    search: '',
  });
  // Local date input values (debounced into filters)
  const [dateFrom, setDateFrom] = useState(daysAgoStr(7));
  const [dateTo, setDateTo] = useState(todayStr());
  const dateDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [sites, setSites] = useState<Pick<AlarmSite, 'id' | 'name'>[]>([]);
  const [liveCount, setLiveCount] = useState(0);
  const [events, setEvents] = useState<HistoryEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<HistoryEvent | null>(null);

  // Keep refs for use inside realtime callback (avoids stale closure)
  const filtersRef = useRef(filters);
  const pageRef = useRef(page);
  const sitesRef = useRef(sites);
  filtersRef.current = filters;
  pageRef.current = page;
  sitesRef.current = sites;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Fetch events
  const doFetch = useCallback(async (f: HistoryFilters, p: number) => {
    setLoading(true);
    try {
      const { data, total: t } = await fetchHistoryEvents(f, p, PAGE_SIZE);
      setEvents(data);
      setTotal(t);
    } catch (e) {
      console.error('[ArcHistory] fetch error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mount-only: serialized init — sites then history events — avoids concurrent
  // auth-lock requests that cause a 5s deadlock cascade on navigation.
  // Unique channel 'arc-history-live' prevents conflict with ArcRealtimeProvider.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loadedSites = await fetchAllSites().catch((): Pick<AlarmSite, 'id' | 'name'>[] => []);
      if (cancelled) return;
      setSites(loadedSites);
      await doFetch(filtersRef.current, pageRef.current);
    })();

    const unsubscribe = subscribeToArcEvents((event: ArcEvent) => {
      const f = filtersRef.current;
      const receivedDate = event.received_at.split('T')[0];
      if (f.dateFrom && receivedDate < f.dateFrom) return;
      if (f.dateTo && receivedDate > f.dateTo) return;
      if (f.siteId && event.site_id !== f.siteId) return;
      if (f.severity && event.severity !== f.severity) return;
      if (f.category && event.event_category !== f.category) return;
      if (f.status === 'pending' && event.acknowledged_at !== null) return;
      if (f.status === 'acknowledged' && (event.acknowledged_at === null || event.resolved_at !== null)) return;
      if (f.status === 'resolved' && event.resolved_at === null) return;

      const site = sitesRef.current.find(s => s.id === event.site_id);
      const enriched: HistoryEvent = {
        ...event,
        alarm_sites: site ? { name: site.name, account_number: null } : null,
        ack_profile: null,
        res_profile: null,
      };

      setTotal(t => t + 1);
      if (pageRef.current === 0) {
        setEvents(prev => [enriched, ...prev]);
      } else {
        setLiveCount(c => c + 1);
      }
    }, undefined, 'arc-history-live');

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []); // mount only

  // Re-fetch when filters or page change — skips initial mount (handled above)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    doFetch(filters, page);
  }, [filters, page, doFetch]);

  function setDateFilter(key: 'dateFrom' | 'dateTo', value: string) {
    if (key === 'dateFrom') setDateFrom(value);
    else setDateTo(value);
    if (dateDebounce.current) clearTimeout(dateDebounce.current);
    dateDebounce.current = setTimeout(() => {
      setFilters(f => ({ ...f, [key]: value }));
      setPage(0);
    }, 600);
  }

  function handleSearchChange(value: string) {
    setSearchInput(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setFilters(f => ({ ...f, search: value.trim() }));
      setPage(0);
    }, 500);
  }

  // Derived stats (no more client-side text search — search is server-side)
  const displayEvents = events;

  const criticalCount = displayEvents.filter(e => e.severity === 'CRITICAL').length;
  const alarmCount = displayEvents.filter(e => e.severity === 'ALARM').length;
  const resolvedCount = displayEvents.filter(e => e.resolved_at).length;
  const pendingCount = displayEvents.filter(e => !e.acknowledged_at).length;
  const avgResponse = (() => {
    const times = displayEvents.filter(e => e.response_time_seconds !== null).map(e => e.response_time_seconds!);
    return times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : null;
  })();

  return (
    <div className="h-full flex flex-col p-6 print:p-2">
      {/* ARC module sub-navigation */}
      <ArcSubNav className="print:hidden" />
      {/* ── Page Header ─────────────────────────────── */}
      <div className="flex items-start justify-between mb-5 print:mb-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarRange className="h-7 w-7" />
            Historial ARC
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Historial de eventos • {filters.dateFrom} al {filters.dateTo}
          </p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={() => exportExcel(events)} disabled={events.length === 0}>
            <Download className="h-4 w-4 mr-1.5" />
            Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportCSV(events)} disabled={events.length === 0}>
            <Download className="h-4 w-4 mr-1.5" />
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => buildMailto(events, filters)} disabled={events.length === 0}>
            <Mail className="h-4 w-4 mr-1.5" />
            Email
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} disabled={events.length === 0}>
            <PrinterIcon className="h-4 w-4 mr-1.5" />
            Imprimir PDF
          </Button>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────── */}
      <Card className="mb-4 print:hidden">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Desde</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={e => setDateFilter('dateFrom', e.target.value)}
                className="w-40"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Hasta</label>
              <Input
                type="date"
                value={dateTo}
                onChange={e => setDateFilter('dateTo', e.target.value)}
                className="w-40"
              />
            </div>

            <div className="flex flex-col gap-1 min-w-[180px]">
              <label className="text-xs text-muted-foreground font-medium">Cliente / Sitio</label>
              <Select
                value={filters.siteId || '__all__'}
                onValueChange={v => { setFilters(f => ({ ...f, siteId: v === '__all__' ? '' : v })); setPage(0); }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Todos los sitios" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los sitios</SelectItem>
                  {sites.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Severidad</label>
              <Select
                value={filters.severity || '__all__'}
                onValueChange={v => { setFilters(f => ({ ...f, severity: v === '__all__' ? '' : v })); setPage(0); }}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  <SelectItem value="CRITICAL">Crítico</SelectItem>
                  <SelectItem value="ALARM">Alarma</SelectItem>
                  <SelectItem value="WARNING">Advertencia</SelectItem>
                  <SelectItem value="INFO">Información</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Categoría</label>
              <Select
                value={filters.category || '__all__'}
                onValueChange={v => { setFilters(f => ({ ...f, category: v === '__all__' ? '' : v })); setPage(0); }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Estado</label>
              <Select
                value={filters.status}
                onValueChange={v => { setFilters(f => ({ ...f, status: v as HistoryFilters['status'] })); setPage(0); }}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="acknowledged">En proceso</SelectItem>
                  <SelectItem value="resolved">Cerrados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground self-center" />}

            {/* Text search */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Buscar</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Código, cliente, resolución..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-8 w-52 h-9 text-sm"
                />
              </div>
            </div>

            {/* Quick range presets */}
            <div className="flex gap-1 ml-auto">
              {[
                { label: 'Hoy', from: todayStr(), to: todayStr() },
                { label: '7d', from: daysAgoStr(7), to: todayStr() },
                { label: '30d', from: daysAgoStr(30), to: todayStr() },
              ].map(preset => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className="text-xs h-8 px-2"
                  onClick={() => {
                    setDateFrom(preset.from);
                    setDateTo(preset.to);
                    setFilters(f => ({ ...f, dateFrom: preset.from, dateTo: preset.to }));
                    setPage(0);
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Stats Row ─────────────────────────────────── */}
      {!loading && total > 0 && (
        <div className="flex items-center gap-6 mb-4 px-1 print:mb-2">
          <StatBadge label="Total" value={total} color="text-foreground" />
          <div className="w-px h-8 bg-border" />
          <StatBadge label="Críticos" value={criticalCount} color="text-red-600" />
          <StatBadge label="Alarmas" value={alarmCount} color="text-orange-500" />
          <div className="w-px h-8 bg-border" />
          <StatBadge label="Cerrados" value={resolvedCount} color="text-green-600" />
          <StatBadge label="Pendientes" value={pendingCount} color="text-amber-600" />
          {avgResponse !== null && (
            <>
              <div className="w-px h-8 bg-border" />
              <div className="text-center">
                <div className="text-2xl font-bold">{fmtResponseTime(avgResponse)}</div>
                <div className="text-xs text-muted-foreground">T. resp. promedio</div>
              </div>
            </>
          )}
          <div className="ml-auto text-sm text-muted-foreground print:hidden">
            Página {page + 1} de {Math.max(1, totalPages)} · {total} registros
          </div>
        </div>
      )}

      {/* ── Print Header ─────────────────────────────── */}
      <div className="hidden print:block mb-4 border-b pb-2">
        <h2 className="text-lg font-bold">Historial ARC — {filters.dateFrom} al {filters.dateTo}</h2>
        <p className="text-sm text-muted-foreground">
          Total: {total} eventos · Críticos: {criticalCount} · Cerrados: {resolvedCount}
        </p>
      </div>

      {/* ── Table ─────────────────────────────────────── */}
      <div className="flex-1 overflow-auto rounded-lg border">
        {/* Table header */}
        <div className="grid grid-cols-[90px_65px_1fr_75px_1fr_80px_75px_90px_75px] gap-2 px-3 py-2 bg-background border-b text-xs font-semibold text-muted-foreground sticky top-0 z-10 shadow-sm print:static">
          <div>Fecha</div>
          <div>Hora</div>
          <div>Cliente</div>
          <div className="text-center">Código</div>
          <div>Descripción</div>
          <div className="text-center">Severidad</div>
          <div className="text-center">Zona</div>
          <div className="text-center">Estado</div>
          <div className="text-center">T. Resp.</div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span className="text-sm">Cargando...</span>
          </div>
        )}

        {/* Empty state */}
        {!loading && displayEvents.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
            <Filter className="h-10 w-10 opacity-30" />
            <p className="text-sm">Sin eventos para los filtros seleccionados</p>
          </div>
        )}

        {/* Rows */}
        {!loading && displayEvents.map((e, idx) => {
          const status = getStatus(e);
          const statusCfg = STATUS_CFG[status];
          const isCritical = e.severity === 'CRITICAL';

          return (
            <div
              key={e.id}
              onClick={() => setSelectedEvent(e)}
              className={cn(
                'grid grid-cols-[90px_65px_1fr_75px_1fr_80px_75px_90px_75px] gap-2 px-3 py-2 border-b last:border-b-0 text-sm',
                'hover:bg-muted/40 transition-colors cursor-pointer',
                isCritical && !e.resolved_at && 'bg-red-500/5',
                idx % 2 === 0 && !isCritical && 'bg-background',
                idx % 2 !== 0 && !isCritical && 'bg-muted/10',
              )}
            >
              {/* Fecha */}
              <div className="font-mono text-xs text-muted-foreground flex items-center">
                {new Date(e.received_at).toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: 'America/Guatemala' })}
              </div>
              {/* Hora */}
              <div className="font-mono text-xs text-muted-foreground flex items-center">
                {new Date(e.received_at).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'America/Guatemala' })}
              </div>

              {/* Cliente */}
              <div className="flex items-center min-w-0">
                <div className="truncate">
                  <span className="font-semibold text-foreground">
                    {e.alarm_sites?.name ?? <span className="text-muted-foreground italic">Sin asignar</span>}
                  </span>
                  {e.alarm_sites?.account_number && (
                    <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                      [{e.alarm_sites.account_number}]
                    </span>
                  )}
                </div>
              </div>

              {/* Código CID */}
              <div className="flex items-center justify-center">
                <span className={cn('font-mono text-xs font-semibold', SEVERITY_COLORS[e.severity])}>
                  {e.cid_code}
                </span>
              </div>

              {/* Descripción */}
              <div className="flex items-center min-w-0">
                <span className="truncate text-sm">{getCidDescription(e.cid_code)}</span>
              </div>

              {/* Severidad */}
              <div className="flex items-center justify-center">
                <span className={cn('text-xs font-medium', SEVERITY_COLORS[e.severity])}>
                  {SEVERITY_LABELS[e.severity] ?? e.severity}
                </span>
              </div>

              {/* Zona */}
              <div className="flex items-center justify-center text-xs text-muted-foreground">
                {e.zone_no != null ? (
                  <span title={e.zone_name ?? undefined}>Z{e.zone_no}</span>
                ) : '-'}
              </div>

              {/* Estado */}
              <div className="flex items-center justify-center">
                <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', statusCfg.cls)}>
                  {statusCfg.label}
                </span>
              </div>

              {/* Tiempo respuesta */}
              <div className="flex items-center justify-center text-xs text-muted-foreground font-mono">
                {fmtResponseTime(e.response_time_seconds)}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Pagination ────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 print:hidden">
          <span className="text-sm text-muted-foreground">
            Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline" size="sm"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline" size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Event detail modal ───────────────────────── */}
      {selectedEvent && <HistoryEventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
    </div>
  );
}
